import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    LayoutDashboard, FileText, FlaskConical, FileDown, Lock, Info, MoreHorizontal, X, Microscope,
} from "lucide-react";
import { reportUrl } from "../../service/CfriService";

// Nav for the CFRI portal. Deliberately linked from nowhere else in the app:
// the portal is reached only by the /cfri link sent to CFRI.
function navItems(proposals) {
    return [
        { key: "overview", label: "Overview", mobileLabel: "Overview", icon: LayoutDashboard, path: "/cfri" },
        ...proposals.map((p) => ({
            key: p.id, group: "proposals", label: p.shortName, mobileLabel: p.shortName, icon: FileText,
            path: `/cfri/proposal/${p.id}`, title: p.title,
        })),
        { key: "method", group: "reference", label: "How this was computed", mobileLabel: "Method", icon: FlaskConical, path: "/cfri/method" },
    ];
}

export default function CfriSidebar({ proposals = [], active, onLock }) {
    const navigate = useNavigate();
    const [moreOpen, setMoreOpen] = useState(false);

    const items = navItems(proposals);
    const go = (path) => { setMoreOpen(false); navigate(path); };
    const openReport = () => { setMoreOpen(false); window.open(reportUrl(), "_blank", "noopener"); };

    const renderNavItem = (item) => {
        const Icon = item.icon;
        return (
            <div
                key={item.key}
                className={`nav-item${item.key === active ? " nav-item-active" : ""}`}
                onClick={() => go(item.path)}
                title={item.title}
            >
                <Icon size={14} />
                <span style={{ flex: 1 }}>{item.label}</span>
            </div>
        );
    };

    const mobilePrimary = items.slice(0, 4);
    const mobileRest = items.slice(4);

    return (
        <>
            <aside className="sidebar">
                <div className="sidebar-logo" onClick={() => navigate("/cfri")} style={{ cursor: "pointer" }}>
                    <div className="sidebar-logo-icon">
                        <Microscope size={20} color="var(--brand-text)" />
                    </div>
                    <span className="sidebar-logo-name">CFRI Review</span>
                </div>

                <nav className="sidebar-nav">
                    {items.filter((i) => !i.group).map(renderNavItem)}
                    {proposals.length > 0 && <div className="funder-nav-section">Proposals</div>}
                    {items.filter((i) => i.group === "proposals").map(renderNavItem)}
                    <div className="funder-nav-section">Reference</div>
                    {items.filter((i) => i.group === "reference").map(renderNavItem)}
                    <div className="nav-item" onClick={openReport}>
                        <FileDown size={14} />
                        <span style={{ flex: 1 }}>Printable report</span>
                    </div>
                </nav>

                <div className="sidebar-bottom">
                    <div className="nav-item" onClick={() => navigate("/about")}>
                        <Info size={14} />
                        About DeepThought
                    </div>
                    <div className="nav-item" onClick={onLock}>
                        <Lock size={14} />
                        Lock this report
                    </div>
                </div>
            </aside>

            <nav className="mobile-nav">
                {mobilePrimary.map((item) => {
                    const Icon = item.icon;
                    return (
                        <button
                            key={item.key}
                            className={`mobile-nav-item${item.key === active ? " mobile-nav-item-active" : ""}`}
                            onClick={() => go(item.path)}
                        >
                            <Icon size={22} />
                            <span className="mobile-nav-label">{item.mobileLabel}</span>
                        </button>
                    );
                })}
                <button
                    className={`mobile-nav-item${moreOpen || mobileRest.some((i) => i.key === active) ? " mobile-nav-item-active" : ""}`}
                    onClick={() => setMoreOpen((o) => !o)}
                >
                    <MoreHorizontal size={22} />
                    <span className="mobile-nav-label">More</span>
                </button>
            </nav>

            {moreOpen && (
                <>
                    <div className="funder-more-backdrop" onClick={() => setMoreOpen(false)} />
                    <div className="funder-more-sheet" role="menu">
                        <div className="funder-more-header">
                            <span>CFRI Review</span>
                            <button className="funder-more-close" onClick={() => setMoreOpen(false)} aria-label="Close menu"><X size={18} /></button>
                        </div>
                        {mobileRest.map((item) => {
                            const Icon = item.icon;
                            return (
                                <button key={item.key} className="funder-more-item" onClick={() => go(item.path)}>
                                    <Icon size={16} />
                                    <span style={{ flex: 1, textAlign: "left" }}>{item.label}</span>
                                </button>
                            );
                        })}
                        <button className="funder-more-item" onClick={openReport}>
                            <FileDown size={16} />
                            <span style={{ flex: 1, textAlign: "left" }}>Printable report</span>
                        </button>
                        <button className="funder-more-item" onClick={() => go("/about")}>
                            <Info size={16} />
                            <span style={{ flex: 1, textAlign: "left" }}>About DeepThought</span>
                        </button>
                        <button className="funder-more-item" onClick={() => { setMoreOpen(false); onLock(); }}>
                            <Lock size={16} />
                            <span style={{ flex: 1, textAlign: "left" }}>Lock this report</span>
                        </button>
                    </div>
                </>
            )}
        </>
    );
}
